import React, { useState, useEffect } from 'react';
import {
  Box, Grid, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useOrder } from '../../context';
import { userService } from '../../../../shared/services/users/usersServices';
import { useAuth } from '../../../auth';

interface OrderFormProps {
  orderId?: string;
}

export function OrderForm({ orderId }: OrderFormProps) {
  const { createOrder, updateOrder, isSubmitting } = useOrder();
  const auth = useAuth();

  const [form, setForm] = useState({
    origin: '',
    recipientName: '',
    receivedBy: '',
    receivedAt: '',
    userId: '',
    orderCode: '',
  });

  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users', 'customer', auth.selectedTenant],
    queryFn: () => userService.list({ typeFlag: 'customer', tenantId: auth.selectedTenant.id }),
    select: res => res.data || [],
  });

  const { data: orderData, isLoading: isOrderLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => orderId ? fetch(`/api/orders/${orderId}`).then(res => res.json()) : Promise.resolve(null),
    enabled: !!orderId,
  });

  useEffect(() => {
    if (orderData) {
      setForm({
        origin: orderData.origin || '',
        recipientName: orderData.recipientName || '',
        receivedBy: orderData.receivedBy || '',
        receivedAt: orderData.receivedAt ? orderData.receivedAt.slice(0, 16) : '',
        userId: orderData.user?.id || '',
        orderCode: orderData.orderCode || '',
      });
    }
  }, [orderData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name as string]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (orderId) {
   
      await updateOrder(orderId, form);
    } else {
      await createOrder(form as any);
    }
  };

  if (isOrderLoading) return <div>Carregando...</div>;

  return (
    <form onSubmit={handleSubmit}>
      <Typography fontWeight={700} mb={2}>{orderId ? 'Editar Encomenda' : 'Nova Encomenda'}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Origem"
            name="origin"
            fullWidth
            value={form.origin}
            onChange={handleChange}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Destinatário"
            name="recipientName"
            fullWidth
            value={form.recipientName}
            onChange={handleChange}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Recebido por"
            name="receivedBy"
            fullWidth
            value={form.receivedBy}
            onChange={handleChange}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Data/Hora Recebimento"
            name="receivedAt"
            type="datetime-local"
            fullWidth
            value={form.receivedAt}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Morador</InputLabel>
            <Select
              name="userId"
              value={form.userId}
              onChange={handleChange as any}
              label="Morador"
              disabled={isLoadingUsers || !!orderId}
            >
              {users.map((user: any) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Código da Encomenda"
            name="orderCode"
            fullWidth
            value={form.orderCode}
            onChange={handleChange}
            required
          />
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button variant="outlined" color="inherit" type="button">Cancelar</Button>
        <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>Salvar</Button>
      </Box>
    </form>
  );
}